import { MagicAuth, parseJWT } from '@magicdoorinc/auth';
import { PermissionMatcher } from '@magicdoorinc/rbac';
import { CompanyBusinessMode } from '~/swagger/portal/Api';
import { syncThisHostAccounts, serializeThisHostRoster } from '~/utils/crossHostAccounts';
import { redirectToBusinessModeHostOnLogin, IS_HOA_DEMO_MODE, IS_PORTAL_HOST } from '~/utils/hoaDemoMode';
import { decideHostCorrection, pickHandoffFallbackAccount } from '~/utils/hostAccountCorrection';
import { uploadAuthLog } from '~/utils/sentry';
import type { MagicAuthConfig } from '@magicdoorinc/auth';
import type { AuthConfigDto } from '~/swagger/portal/Api';

const getAuthRoot = async () => {
  const response = await fetch('/api/auth/configuration');
  const data: AuthConfigDto = await response.json();

  const authRoot = new URL(data.authorizeUrl).origin;
  return authRoot;
};

export const getAuthConfig = () => {
  const origin = window.location.origin;
  return {
    appRoot: '/api',
    authRoot: getAuthRoot(),
    callbackUrl: `${origin}/auth/callback`,
    postLoginUrl: '/account/api-keys',
    clientId: 'company_portal',
    logger: {
      customHandler: uploadAuthLog,
      enabled: true,
    },
  } as MagicAuthConfig;
};

export const auth = new MagicAuth(getAuthConfig());

export type SwitchAccountResult = { status: 'switched'; accountId: string | null };

/** company_id of the active account (the `companiesId` the rest of the app uses). */
const activeCompanyIdFromToken = (): string | null => {
  const id = auth.storage.getActiveAccountId();
  return id ? (auth.storage.getAccountInfo(id)?.company_id ?? null) : null;
};

const LAST_ACCOUNT_BY_MODE_KEY = 'lastAccountByMode';

const getLastAccountByMode = (): Partial<Record<CompanyBusinessMode, string>> => {
  try {
    return JSON.parse(localStorage.getItem(LAST_ACCOUNT_BY_MODE_KEY) ?? '{}');
  } catch {
    return {};
  }
};

/**
 * Remember the last account made active on THIS device for a given business mode,
 * so when the user lands on a portal host we can restore the account they last
 * used on THAT portal rather than whatever happens to be globally active. Keyed by
 * the account's known business mode (from {@link getCompanyBusinessModes}); we only
 * record it once the mode is known, since that's the only thing we key host
 * correction off. See {@link correctActiveAccountForHost}.
 */
const rememberLastAccountForMode = (accountId: string | null): void => {
  if (!accountId) return;
  const companyId = auth.storage.getAccountInfo(accountId)?.company_id;
  const mode = companyId ? getCompanyBusinessModes()[companyId] : undefined;
  if (mode === undefined) return;
  try {
    const map = getLastAccountByMode();
    if (map[mode] === accountId) return;
    map[mode] = accountId;
    localStorage.setItem(LAST_ACCOUNT_BY_MODE_KEY, JSON.stringify(map));
  } catch {
    // ignore storage errors — last-active memory is best-effort
  }
};

/**
 * Switch the current tab to another already-signed-in account (login+company).
 * Only swaps the active account for this tab; the reactive auth layer refreshes
 * the token on demand (or redirects to sign-in if its refresh token is gone).
 *
 * @param targetAccountId composite account id from `getSignedInAccounts()`.
 */
export const switchToAccount = async (targetAccountId: string): Promise<SwitchAccountResult> => {
  auth.storage.setActiveAccountId(targetAccountId);
  const companyId = activeCompanyIdFromToken();
  if (companyId) localStorage.setItem('companiesId', companyId);
  const activeId = auth.storage.getActiveAccountId();
  rememberLastAccountForMode(activeId);
  return { status: 'switched', accountId: activeId };
};

export type HostCorrectionResult = 'unchanged' | 'switched' | 'needs-signin';

/** The business mode this host's portal serves. */
const THIS_HOST_MODE: CompanyBusinessMode = IS_HOA_DEMO_MODE ? CompanyBusinessMode.Hoa : CompanyBusinessMode.PropertyManagement;

const accountMode = (accountId: string): CompanyBusinessMode | undefined => {
  const companyId = auth.storage.getAccountInfo(accountId)?.company_id;
  return companyId ? getCompanyBusinessModes()[companyId] : undefined;
};

/**
 * On load, make the active account match THIS portal host. The two portals are
 * separate origins, but a cross-host login handoff can leave this origin signed in
 * with the sibling portal's account still active — so typing this host's URL would
 * otherwise show the wrong portal's account (see the cross-host handoff in
 * {@link redirectToBusinessModeHostOnLogin}).
 *
 * Behavior (business mode is only KNOWN for companies loaded before in this
 * browser; the JWT doesn't carry it):
 *  - active account's mode matches this host, or is unknown -> leave it ('unchanged').
 *    We never force-switch away from an account we can't prove is wrong.
 *  - active account's mode is the OTHER host's -> switch to the last account used
 *    on THIS host (falling back to any signed-in account whose known mode matches),
 *    and return 'switched' so the caller reloads.
 *  - no signed-in account matches this host -> 'needs-signin' so the caller sends
 *    the user to this host's sign-in rather than showing the wrong portal.
 *
 * No-op unless this host is one of the two recognized portal hosts. On a
 * single-portal deployment (bare `localhost`, an IP, a `demo.` host) there is no
 * sibling to cross to and no handoff ever runs, so an account of either mode is
 * legitimately at home here — correcting it would wrongly bounce the user.
 */
export const correctActiveAccountForHost = (): HostCorrectionResult => {
  if (!IS_PORTAL_HOST) return 'unchanged';
  const decision = decideHostCorrection({
    activeAccountId: auth.storage.getActiveAccountId(),
    thisHostMode: THIS_HOST_MODE,
    accountIds: auth.storage.listAccountIds(),
    modeOf: accountMode,
    lastAccountForThisHost: getLastAccountByMode()[THIS_HOST_MODE],
  });
  if (decision.action === 'unchanged') return 'unchanged';
  if (decision.action === 'needs-signin') return 'needs-signin';

  auth.storage.setActiveAccountId(decision.targetAccountId);
  const companyId = activeCompanyIdFromToken();
  if (companyId) localStorage.setItem('companiesId', companyId);
  rememberLastAccountForMode(auth.storage.getActiveAccountId());
  return 'switched';
};

/**
 * Right before we hand the active account off to the SIBLING portal host, repoint
 * THIS origin's active account to one that belongs to THIS host — so this origin
 * isn't left with the other portal's account active (the state that made typing
 * this host's URL show the wrong portal). If this origin has no same-host account
 * to fall back to, we leave it as-is; {@link correctActiveAccountForHost} sends the
 * user to sign-in on the next visit here. Best-effort and purely local (no network).
 */
export const demoteActiveAccountForHandoff = (): void => {
  const fallback = pickHandoffFallbackAccount({
    activeAccountId: auth.storage.getActiveAccountId(),
    thisHostMode: THIS_HOST_MODE,
    accountIds: auth.storage.listAccountIds(),
    modeOf: accountMode,
  });
  if (!fallback) return;
  auth.storage.setActiveAccountId(fallback);
  const companyId = activeCompanyIdFromToken();
  if (companyId) localStorage.setItem('companiesId', companyId);
  rememberLastAccountForMode(auth.storage.getActiveAccountId());
};

/**
 * After an in-place account switch, land the user on the correct portal HOST for
 * the now-active company's business mode.
 *
 * A cached switch only swaps tokens in THIS tab — it doesn't change the host. So
 * switching to an HOA company while on the property host (or vice versa) would
 * leave the user logged into the right company but on the wrong portal (wrong
 * theme/nav/labels). This reuses the login-time handoff
 * ({@link redirectToBusinessModeHostOnLogin}) to send them to the matching host
 * via a silent, single-use interaction code, and returns `true` when it did so.
 * When the company already matches the current host (or `businessMode` is
 * unknown), it returns `false` and the caller should reload in place.
 *
 * `businessMode` comes from the switcher row (cached per company), since the new
 * company isn't loaded yet at switch time. If it's undefined (a company we've
 * never loaded), no host hop happens here; the user stays put until the mode is
 * learned, and {@link correctActiveAccountForHost} keeps each host showing an
 * account that belongs to its portal on subsequent loads.
 */
export const routeToBusinessModeHostAfterSwitch = async (
  businessMode: CompanyBusinessMode | undefined,
  postSwitchPath = '/'
): Promise<boolean> => {
  const handedOff = await redirectToBusinessModeHostOnLogin(
    businessMode,
    postSwitchPath,
    async () => {
      const token = await auth.getInteractionToken();
      if (!token.interactionCode) throw new Error('No interaction code returned');
      return token.interactionCode;
    },
    serializeThisHostRoster(getSignedInAccounts())
  );
  if (handedOff) demoteActiveAccountForHandoff();
  return handedOff;
};

/**
 * Send the user to a fresh sign-in to ADD another account (a different login
 * and/or company), WITHOUT clearing the current account. Preserves the current
 * location as the redirect target.
 */
export const addAccount = (redirectUrl?: string) => {
  const redirect = redirectUrl ?? location.pathname + location.search;
  const target = `/auth/signin?redirect=${encodeURIComponent(redirect)}`;
  // Navigating to the page we're already on is a no-op in some browsers, which
  // would leave the user stuck. Force a reload when the target matches.
  if (location.pathname + location.search === target) {
    window.location.reload();
    return;
  }
  window.location.assign(target);
};

/** Composite ids of all accounts signed in on this device. */
export const getSignedInAccountIds = (): string[] => auth.storage.listAccountIds();

/** Active account's composite id. */
export const getActiveAccountId = (): string | null => auth.storage.getActiveAccountId();

/** Active account's company_id (for app code that keys off company). */
export const getActiveCompanyId = (): string | null => activeCompanyIdFromToken();

const permissionMatcher = new PermissionMatcher();

/** Permission strings from the active account's JWT — the same list the backend enforces. */
export const getActivePermissions = (): string[] => {
  const accountId = auth.storage.getActiveAccountId();
  const claim = accountId ? auth.storage.getAccountInfo(accountId)?.permissions : undefined;
  if (!claim) return [];
  return Array.isArray(claim)
    ? claim
    : claim
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean);
};

/** Synchronous permission check against the active account's JWT. */
export const hasActivePermission = (resource: string, action: string): boolean =>
  permissionMatcher.checkPermission(getActivePermissions(), `${resource}:${action}`);

/**
 * True while a MagicDoor employee is signed in AS this user. Some actions are refused
 * server-side in that state (issuing an API key, for one), so callers can say why up front
 * instead of letting the request fail.
 */
export const isShadowSession = (): boolean => {
  const token = auth.storage.getTokenStorage().access_token;
  if (!token) return false;
  try {
    return parseJWT(token).shadow === 'true';
  } catch {
    return false;
  }
};

export type SignedInAccount = {
  /** Composite account id (`<sub>:<company_id>`) — the switch key. */
  id: string;
  /** company_id of this account. */
  companyId: string;
  /** Company display name (remembered) or company id. */
  companyName: string;
  /** The signed-in user's name (from JWT), if present. */
  userName: string;
  /** The signed-in user's email (from JWT), if present. */
  email: string;
  /**
   * Primary label for the switcher row. Prefers the user's name/email so that
   * two different users of the SAME company are distinguishable; falls back to
   * the company name.
   */
  label: string;
  /** Secondary label: company name (so the row shows both person and company). */
  subLabel: string;
  isActive: boolean;
  /**
   * The company's business mode, if we've learned it for this companyId in this
   * browser. Undefined for a company we've never loaded (the JWT doesn't carry
   * it), so the switcher just omits the type badge for those rows.
   */
  businessMode: CompanyBusinessMode | undefined;
};

/**
 * All accounts currently signed in on this device, for the account switcher.
 * Each is a (login + company) pair. Labeled by the user (name/email) as the
 * primary line and the company as the secondary line, so two users of the same
 * company are always distinguishable (Google-style).
 */
export const getSignedInAccounts = (): SignedInAccount[] => {
  const activeId = auth.storage.getActiveAccountId();
  const names = getCompanyNames();
  const modes = getCompanyBusinessModes();
  const accounts = auth.storage.listAccountInfos().map(({ accountId, info }) => {
    const companyId = info?.company_id ?? '';
    const companyName = names[companyId] || companyId || accountId;
    const userName = (info as { name?: string } | null)?.name ?? '';
    const email = info?.email ?? '';
    // Primary line identifies the PERSON; that's what disambiguates same-company
    // logins. Fall back to company name only when we have no user identity.
    const label = userName || email || companyName;
    // Avoid repeating the same string on both lines.
    const subLabel = label === companyName ? '' : companyName;
    const isActive = accountId === activeId;
    return {
      id: accountId,
      companyId,
      companyName,
      userName,
      email,
      label,
      subLabel,
      isActive,
      businessMode: modes[companyId],
    };
  });
  // Publish this host's accounts to the shared (parent-domain) roster so the
  // sibling portal host can show them. Reading the switcher is the natural sync
  // point: it runs on load and re-runs after every switch.
  syncThisHostAccounts(accounts);
  return accounts;
};

const COMPANY_NAMES_KEY = 'companyNames';

/**
 * Remember a company's display name keyed by id, so the switcher can label
 * cached companies (their tokens carry the id, not the name). Call this whenever
 * we learn a company's name (login company list, current company load).
 */
export const rememberCompanyName = (companyId: string, name: string): void => {
  try {
    const map = getCompanyNames();
    if (map[companyId] === name) return;
    map[companyId] = name;
    localStorage.setItem(COMPANY_NAMES_KEY, JSON.stringify(map));
  } catch {
    // ignore storage errors — labels are best-effort
  }
};

export const getCompanyNames = (): Record<string, string> => {
  try {
    return JSON.parse(localStorage.getItem(COMPANY_NAMES_KEY) ?? '{}');
  } catch {
    return {};
  }
};

const COMPANY_BUSINESS_MODES_KEY = 'companyBusinessModes';

/**
 * Remember a company's business mode keyed by id, so the account switchers can
 * badge cached companies as HOA/Property. The JWT doesn't carry the mode, so we
 * record it whenever we load the company (see CompaniesContext). Best-effort:
 * companies never loaded in this browser simply have no badge.
 */
export const rememberCompanyBusinessMode = (companyId: string, businessMode: CompanyBusinessMode): void => {
  try {
    const map = getCompanyBusinessModes();
    if (map[companyId] !== businessMode) {
      map[companyId] = businessMode;
      localStorage.setItem(COMPANY_BUSINESS_MODES_KEY, JSON.stringify(map));
    }
  } catch {
    // ignore storage errors — badges are best-effort
  }
  // Learning the active account's mode is also when its last-active-per-mode entry
  // first becomes recordable (the JWT alone never carries the mode). Record it so
  // host correction can later restore this as the last account used on its portal.
  const activeId = auth.storage.getActiveAccountId();
  if (activeId && auth.storage.getAccountInfo(activeId)?.company_id === companyId) {
    rememberLastAccountForMode(activeId);
  }
};

export const getCompanyBusinessModes = (): Record<string, CompanyBusinessMode> => {
  try {
    return JSON.parse(localStorage.getItem(COMPANY_BUSINESS_MODES_KEY) ?? '{}');
  } catch {
    return {};
  }
};

export const redirectToAuth = (redirectUrl?: string, options?: { reason?: 'expired' | null; logReason?: string }) => {
  // clearTokens + best-effort revoke (keepalive so replace() does not abort it)
  auth.logout();
  const redirect = redirectUrl ?? location.pathname + location.search;
  // `reason=expired` lets the sign-in page tell the user their session ended
  // unexpectedly. Pass `reason: null` for an intentional logout, where the
  // banner would just be telling the user something they already did.
  const reason = options?.reason === undefined ? 'expired' : options.reason;
  const authUrl = `/auth/signin?redirect=${redirect}${reason ? `&reason=${reason}` : ''}`;
  window.location.replace(authUrl);
};

/**
 * Sign out of EVERY account signed in on this device, not just the active one.
 *
 * `auth.logout()` only revokes and drops the ACTIVE account; the storage layer
 * then promotes the next signed-in account, so a plain sign-out leaves the user
 * logged in as somebody else. Here we make each account active in turn so
 * `logout()` picks up its tokens and revokes them server-side, then drop any
 * account that had nothing to revoke. Revokes are `keepalive` inside the SDK, so
 * they survive the redirect that follows.
 *
 * Best-effort per account: one failed revoke must not strand the rest signed in,
 * so a failure still leaves the local tokens cleared.
 */
export const signOutAllAccounts = async (): Promise<void> => {
  for (const accountId of auth.storage.listAccountIds()) {
    auth.storage.setActiveAccountId(accountId);
    try {
      await auth.logout();
    } catch {
      auth.storage.removeAccountItem(accountId);
    }
  }
  for (const accountId of auth.storage.listAccountIds()) {
    auth.storage.removeAccountItem(accountId);
  }
  try {
    localStorage.removeItem(LAST_ACCOUNT_BY_MODE_KEY);
    localStorage.removeItem('companiesId');
  } catch {
    // ignore storage errors — tokens are already gone, which is what signs the user out
  }
};

export const isAuthSessionError = (error: unknown): boolean =>
  error instanceof Error && ['No active account', 'No token available', 'No token type available'].includes(error.message);

/**
 * Single-use scratch values the OAuth authorization-code flow writes to
 * sessionStorage on the way out to `/oauth/authorize`, and reads back on
 * `/auth/callback`. A flow that's abandoned before the callback (closed tab,
 * back button, an earlier broken build) leaves these behind, which can wedge
 * the next attempt with "Invalid state parameter" / "PKCE verifier not found".
 *
 * Call this when starting a fresh sign-in on the credentials screen — never on
 * `/auth/callback`, which still needs them. A subsequent `initiateOAuthFlow`
 * regenerates all three before redirecting, so clearing here is always safe.
 */
export const clearStaleAuthFlowState = () => {
  sessionStorage.removeItem('pkce_verifier');
  sessionStorage.removeItem('state');
  sessionStorage.removeItem('redirect');
};
